import React from 'react'
import { motion } from 'framer-motion'
import { Download, Mail, Phone, Globe, Briefcase, Code, Award } from 'lucide-react'
import { aboutData } from '../data/about'

const Resume = () => {
  const { personal, skillCategories, timeline } = aboutData

  return (
    <div className="min-h-screen pt-16 bg-base-100">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-gradient">
            {personal.name}
          </h1>
          <p className="text-2xl text-base-content/70 mb-6">{personal.title}</p>
          <p className="text-xl text-base-content/60 mb-8">{personal.subtitle}</p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href={`tel:${personal.phone}`} className="btn btn-outline btn-sm">
              <Phone className="w-4 h-4 mr-2" />
              {personal.phone}
            </a>
            <a href={`mailto:${personal.email}`} className="btn btn-outline btn-sm">
              <Mail className="w-4 h-4 mr-2" />
              {personal.email}
            </a>
            <a href={`https://${personal.website}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
              <Globe className="w-4 h-4 mr-2" />
              {personal.website}
            </a>
          </div>

          <button className="btn btn-primary">
            <Download className="w-4 h-4 mr-2" />
            Download Resume PDF
          </button>
        </motion.div>

        {/* Experience Timeline */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
            <Briefcase className="w-8 h-8 text-primary" />
            Professional Experience
          </h2>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card glass p-6"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                    <p className="text-base-content/70">{item.period}</p>
                  </div>
                  <span className="badge badge-primary badge-lg mt-2 md:mt-0">{item.year}</span>
                </div>
                <p className="text-base-content/80">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Skills */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-2">
            <Code className="w-8 h-8 text-primary" />
            Technical Skills
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(skillCategories).map(([key, category]) => (
              <div key={key} className="card glass p-6">
                <h3 className="text-xl font-bold mb-4 text-primary">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span className="text-base-content/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Summary */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card glass p-8"
        >
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
            <Award className="w-8 h-8 text-primary" />
            Professional Summary
          </h2>
          {aboutData.bio.paragraphs.map((paragraph, index) => (
            <p key={index} className="text-lg text-base-content/80 mb-4 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </motion.section>
      </div>
    </div>
  )
}

export default Resume
